import { Manifest } from "@equipped/core/core/manifest";
import { FieldDescriptor } from "./fieldTypes";

/**
 * The Pack (manifest) properties panel's field registry — every `Manifest` field
 * except `lessons` (tree-managed, docs/343 §2) and `specVersion` (fixed at `1`,
 * never shown as an editable field). `Manifest` is `z.infer<typeof ManifestSchema>`,
 * so `keyof Manifest` is trustworthy.
 */
export const packFields = {
  id: { section: "Identity", label: "Pack id", kind: "text" },
  title: { section: "Identity", label: "Title", kind: "text" },
  subtitle: { section: "Identity", label: "Subtitle", kind: "text" },
  description: { section: "Identity", label: "Description", kind: "textarea" },
  author: { section: "Identity", label: "Author", kind: "text" },
  version: { section: "Identity", label: "Version (semver)", kind: "text" },
  updated: { section: "Identity", label: "Updated (YYYY-MM-DD)", kind: "text" },
  contentHash: { section: "Identity", label: "Content hash", kind: "text" },
  icon: { section: "Identity", label: "Icon (URL or pack-relative path)", kind: "text" },

  tags: { section: "Discovery", label: "Tags", kind: "stringArray" },
  access: {
    section: "Discovery",
    label: "Access",
    kind: "select",
    options: [
      { value: "free", label: "free" },
      { value: "paid", label: "paid" },
    ],
  },
  languages: { section: "Discovery", label: "Languages this pack ships", kind: "stringArray" },
  defaultLanguage: { section: "Discovery", label: "Default language", kind: "text" },
  related: { section: "Discovery", label: "Related courses (see-also)", kind: "relatedRefs" },
  sameAs: {
    section: "Discovery",
    label: "Same-as key",
    kind: "text",
    help: "Opaque cross-placement equality key — packs sharing this string are treated as the same underlying content.",
  },

  downloadable: { section: "Access & capabilities", label: "Downloadable for offline use", kind: "boolean3" },
  castable: { section: "Access & capabilities", label: "Castable (AirPlay/Chromecast)", kind: "boolean3" },
  tvMedia: {
    section: "Access & capabilities",
    label: "Has TV media (skip-hint)",
    kind: "boolean3",
    help: "false = a hint that this pack is media-less, so the Roku feed walk skips it. Absent/true = walk it (default).",
  },
  features: { section: "Access & capabilities", label: "Feature-flag defaults", kind: "featureFlags" },
  platforms: { section: "Access & capabilities", label: "Platform availability (opt-out)", kind: "recordBoolean" },

  sections: {
    section: "Reading structure",
    label: "Section splitter",
    kind: "sectionsFrom",
    help: "Declares what splits a reading lesson's body into sections in place of/alongside # headings.",
  },
} satisfies Record<Exclude<keyof Manifest, "lessons" | "specVersion">, FieldDescriptor>;
